import { AssumeRoleCommand, STSClient } from '@aws-sdk/client-sts'
import {
    CognitoIdentityCredentialProvider,
    fromCognitoIdentityPool,
} from '@aws-sdk/credential-providers'
import { CognitoAuthSession } from 'amazon-cognito-auth-js'

import {
    APP_CONFIG,
    CONFIGS_BY_ENVIRONMENT,
    QA_AUTOMATION_STS_ASSUME_ROLE,
    STAGE,
} from 'src/config.app'
import { HookAuthenticator } from 'src/pages/scoring-test-ui/authentication/HookAuthenticator'
import { Authenticator } from 'src/services/Authenticator'
import { ClientConfiguration } from 'src/services/aws/DDBHandler'
import { retryAuth } from 'src/services/AxiosInterceptor'

export const REGION = 'us-west-2'

export const getCredentials = async (): Promise<CognitoIdentityCredentialProvider> => {
    const userSession = await retryAuth(() => Authenticator.getValidUserSession())

    const userJwtToken: string = userSession.getIdToken().getJwtToken()
    const cognitoLoginURL = `cognito-idp.${REGION}.amazonaws.com/${
        APP_CONFIG.auth.UserPoolId || ''
    }`

    return fromCognitoIdentityPool({
        identityPoolId: APP_CONFIG.identityPoolId,
        logins: { [cognitoLoginURL]: userJwtToken },
        clientConfig: {
            // https://github.com/aws/aws-sdk-js-v3/issues/2958
            region: REGION,
        },
    })
}

export const getHookCredentials = async (
    stage?: STAGE
): Promise<CognitoIdentityCredentialProvider> => {
    const userSession: CognitoAuthSession = await retryAuth(() =>
        HookAuthenticator.getValidUserSession()
    )

    const userJwtToken: string = userSession.getIdToken().getJwtToken()
    const cognitoLoginURL = `cognito-idp.${REGION}.amazonaws.com/${
        stage
            ? CONFIGS_BY_ENVIRONMENT[stage].hookAuth.UserPoolId || ''
            : APP_CONFIG.hookAuth.UserPoolId || ''
    }`

    return fromCognitoIdentityPool({
        identityPoolId: stage
            ? CONFIGS_BY_ENVIRONMENT[stage].hookConfig.hookIdentityPoolId
            : APP_CONFIG.hookConfig.hookIdentityPoolId,
        logins: {
            [cognitoLoginURL]: userJwtToken,
        },
        clientConfig: {
            // https://github.com/aws/aws-sdk-js-v3/issues/2958
            region: REGION,
        },
    })
}

export const setCrossAccountCredentials = async (): Promise<ClientConfiguration> => {
    const sts = new STSClient({
        region: REGION,
        credentials: await getCredentials(),
    })

    const timestamp = new Date().getTime()
    const params = {
        RoleArn: QA_AUTOMATION_STS_ASSUME_ROLE,
        RoleSessionName: `pulling-test-results-${timestamp}`,
    }

    const data = await sts.send(new AssumeRoleCommand(params))
    if (
        !data.Credentials ||
        !data.Credentials.AccessKeyId ||
        !data.Credentials.SecretAccessKey ||
        !data.Credentials.SessionToken
    ) {
        throw new Error('setCrossAccountCredentials: Failed to assume role')
    }

    return {
        accessKeyId: data.Credentials.AccessKeyId,
        secretAccessKey: data.Credentials.SecretAccessKey,
        sessionToken: data.Credentials.SessionToken,
    }
}
